import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { FiTag, FiX, FiPlus } from 'react-icons/fi';

const TaskTags = ({ taskId, tags: initialTags = [] }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [tags, setTags] = useState(initialTags);
  const [showAddTag, setShowAddTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (taskId) {
      fetchTags();
    } else {
      // Use initial tags if no taskId (for new tasks)
      setTags(initialTags || []);
    }
  }, [taskId]);

  const fetchTags = async () => {
    if (!taskId) return;
    try {
      const response = await api.get(`/tasks/${taskId}/tags`);
      setTags(response.data.tags || []);
    } catch (error) {
      // If 404, task might not have tags yet, use initial tags
      if (error.response?.status === 404) {
        setTags(initialTags || []);
      } else {
        console.error('Error fetching tags:', error);
      }
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTag.trim()) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/tasks/${taskId}/tags`, {
        tag: newTag.trim()
      });
      setTags([...tags, response.data.tag]);
      setNewTag('');
      setShowAddTag(false);
      showSuccess('Tag added successfully!', 'Tag Added');
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to add tag', 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await api.delete(`/tasks/${taskId}/tags/${tagId}`);
      setTags(tags.filter(t => t.id !== tagId));
      showSuccess('Tag removed successfully!', 'Tag Removed');
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to remove tag', 'Error');
    }
  };

  return (
    <div className="mt-4">
      <div className="flex items-center space-x-2 mb-3">
        <FiTag className="text-white/80" size={18} />
        <h3 className="text-white font-semibold">Tags</h3>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <AnimatePresence>
          {tags.map((tag, index) => (
            <motion.span
              key={tag.id || `tag-${index}-${tag.tag}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm text-white"
              style={{ backgroundColor: tag.color || '#6366f1' }}
            >
              <span>{tag.tag || tag}</span>
              {(user?.role === 'admin' || user?.id) && tag.id && (
                <button
                  onClick={() => handleRemoveTag(tag.id)}
                  className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                >
                  <FiX size={14} />
                </button>
              )}
            </motion.span>
          ))}
        </AnimatePresence>
      </div>

      {!showAddTag ? (
        <button
          onClick={() => setShowAddTag(true)}
          className="flex items-center space-x-1 text-white/60 hover:text-white/80 text-sm transition-colors"
        >
          <FiPlus size={16} />
          <span>Add Tag</span>
        </button>
      ) : (
        <form onSubmit={handleAddTag} className="flex space-x-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Enter tag name..."
            className="flex-1 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm"
            autoFocus
          />
          <button
            type="submit"
            disabled={submitting || !newTag.trim()}
            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAddTag(false);
              setNewTag('');
            }}
            className="px-3 py-1.5 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
          >
            Cancel
          </button>
        </form>
      )}
    </div>
  );
};

export default TaskTags;
